'use client'

import { useCallback, useEffect, useRef } from 'react'
import type { SessionLogEvent, SessionLogEventSource } from '@/types/webrtc-session-log.type'

const SESSION_LOG_APPEND_ENDPOINT = '/api/interviews/screening-webrtc/session-log/append'
const SESSION_LOG_FINALIZE_ENDPOINT = '/api/interviews/screening-webrtc/session-log/finalize'

export type SessionLogMetadata = {
    started_at?: string
    webrtc_id?: string
    session_id?: string
}

function getClientToken(): string | null {
    if (typeof sessionStorage === 'undefined') {
        return null
    }
    return sessionStorage.getItem('clientToken')
}

function buildAuthHeaders(): Record<string, string> {
    const token = getClientToken()
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'X-Client-Token': token } : {}),
    }
}

export function useWebRtcSessionLog() {
    const eventsRef = useRef<SessionLogEvent[]>([])
    const metadataRef = useRef<SessionLogMetadata>({})
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const finalizedRef = useRef(false)
    const flushIntervalMs = 15 * 1000

    const setSessionLogMetadata = useCallback((metadata: SessionLogMetadata) => {
        metadataRef.current = { ...metadataRef.current, ...metadata }
    }, [])

    const addSessionLogEvent = useCallback((source: SessionLogEventSource, payload: Record<string, unknown>) => {
        eventsRef.current.push({
            source,
            received_at: new Date().toISOString(),
            payload,
        })
    }, [])

    const flushSessionLogEvents = useCallback(async () => {
        const token = getClientToken()
        if (!token) {
            return
        }

        const eventsToSend = eventsRef.current.splice(0)
        const metadata = metadataRef.current
        const hasMetadata = Boolean(metadata.started_at || metadata.webrtc_id || metadata.session_id)

        if (!eventsToSend.length && !hasMetadata) {
            return
        }

        const body = JSON.stringify({
            clientToken: token,
            events: eventsToSend,
            ...(hasMetadata ? { metadata } : {}),
        })

        try {
            await fetch(SESSION_LOG_APPEND_ENDPOINT, {
                method: 'POST',
                headers: buildAuthHeaders(),
                body,
            })
        } catch {
            eventsRef.current.unshift(...eventsToSend)
        }
    }, [])

    const flushSessionLogEventsSync = useCallback(() => {
        const token = getClientToken()
        if (!token) {
            return
        }

        const eventsToSend = eventsRef.current.splice(0)
        const metadata = metadataRef.current
        const hasMetadata = Boolean(metadata.started_at || metadata.webrtc_id || metadata.session_id)

        if (!eventsToSend.length && !hasMetadata) {
            return
        }

        const body = JSON.stringify({
            clientToken: token,
            events: eventsToSend,
            ...(hasMetadata ? { metadata } : {}),
        })

        if (typeof navigator.sendBeacon !== 'undefined') {
            const blob = new Blob([body], { type: 'application/json' })
            navigator.sendBeacon(SESSION_LOG_APPEND_ENDPOINT, blob)
        } else {
            void fetch(SESSION_LOG_APPEND_ENDPOINT, {
                method: 'POST',
                headers: buildAuthHeaders(),
                body,
            }).catch(() => {
                eventsRef.current.unshift(...eventsToSend)
            })
        }
    }, [])

    const finalizeSessionLog = useCallback(async () => {
        if (finalizedRef.current) {
            return null
        }

        await flushSessionLogEvents()

        const token = getClientToken()
        if (!token) {
            return null
        }

        try {
            const response = await fetch(SESSION_LOG_FINALIZE_ENDPOINT, {
                method: 'POST',
                headers: buildAuthHeaders(),
                body: JSON.stringify({
                    clientToken: token,
                    ...metadataRef.current,
                }),
            })
            const json = await response.json().catch(() => ({}))
            if (!response.ok) {
                return null
            }
            finalizedRef.current = true
            return (json?.data?.decisionLogUrl as string | undefined) ?? null
        } catch {
            return null
        }
    }, [flushSessionLogEvents])

    useEffect(() => {
        const onBeforeUnload = () => {
            flushSessionLogEventsSync()
        }

        window.addEventListener('beforeunload', onBeforeUnload)
        timerRef.current = setInterval(() => {
            void flushSessionLogEvents()
        }, flushIntervalMs)

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload)
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            flushSessionLogEventsSync()
        }
    }, [flushSessionLogEvents, flushSessionLogEventsSync, flushIntervalMs])

    return {
        addSessionLogEvent,
        setSessionLogMetadata,
        flushSessionLogEvents,
        flushSessionLogEventsSync,
        finalizeSessionLog,
    }
}
